import Container from "@/components/ContentContainer/Container";
import { CompareMode } from "@/components/Impact/ImpactPill";
import Message from "@/components/Message/Message";
import PremiumExplore from "@/components/PremiumFeatureComponents/PremiumExplore";
import { useCompanyAccess } from "@/hooks/useCompanySettings";
import useUrlState from "@/hooks/useUrlState";
import useUrlStateNumber from "@/hooks/useUrlStateNumber";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Segmented, Space, Tooltip } from "antd";
import { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetProductScenarios } from "../../useProducts";
import CompareProductScenarios from "./CompareProductScenarios/CompareProductScenarios";
import ManageProductScenarios from "./ManageProductScenarios/ManageProductScenarios";
import styles from "./ProductScenarios.module.scss";

type ScenarioMode = "manage" | "compare";

export default function ProductScenarios({ mode }: { mode: ScenarioMode }) {
  const [multiplier, setMultiplier] = useUrlStateNumber("multiplier", 1);
  const [displayMode, setDisplayMode] = useUrlState<CompareMode>("mode", "absolute");
  const { productId: id } = useParams();
  const productId = Number(id);
  const navigate = useNavigate();
  const access = useCompanyAccess();

  const content: Record<ScenarioMode, ReactNode> = {
    manage: <ManageProductScenarios productId={productId} />,
    compare: (
      <CompareProductScenarios
        productId={productId}
        multiplier={multiplier}
        displayMode={displayMode || "absolute"}
      />
    ),
  };

  const productScenarios = useGetProductScenarios({ productId });

  const compareEnabled = productScenarios.data
    ? productScenarios.data.scenarios.length >= 2
    : false;

  return (
    <div className={styles.ProductScenarios}>
      <Container
        noRoundedTop
        contentClassName={styles["page-header"]}
        right={undefined}
        title="Scenarios"
      >
        <div className={styles.intro}>
          Model alternative BOM scenarios and discover the most effective reduction strategies for
          your products and entire company. Collaborate with your engineers by providing multiple
          BOMs and see instantly how this effects your product material impact.
        </div>
        {access && !!access.includes("product_scenario_builder") && (
          <Space style={{ justifyContent: "space-between" }}>
            <Tooltip
              title={
                !compareEnabled
                  ? "Add a scenario and upload a BOM to enable compare mode."
                  : undefined
              }
              placement="right"
            >
              <Segmented
                options={[
                  { label: "Manage", value: "manage" },
                  { label: "Compare", value: "compare" },
                ]}
                onChange={(value) => navigate("/products/" + productId + "/scenarios/" + value)}
                value={mode}
                disabled={!compareEnabled}
              />
            </Tooltip>
            {mode === "compare" && (
              <div className={styles.extraInfo}>
                <div className={styles.legend}>
                  <div className={styles.square}></div>
                  <span>Differences</span>
                  <Tooltip
                    placement="top"
                    title="Highlights if a material or quantity has been changed or replaced compared to other BOM"
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </div>
                <Segmented
                  options={[
                    { label: "1x", value: 1 },
                    { label: "1000x", value: 1000 },
                  ]}
                  value={multiplier}
                  onChange={setMultiplier}
                ></Segmented>

                <Segmented
                  options={[
                    { label: "Absolute", value: "absolute" },
                    { label: "Percentage", value: "percentage" },
                  ]}
                  value={displayMode}
                  onChange={setDisplayMode}
                ></Segmented>
              </div>
            )}
          </Space>
        )}
      </Container>
      {access && !access.includes("product_scenario_builder") ? (
        <PremiumExplore />
      ) : !!productScenarios.data?.product.proxy_id ? (
        <Container>
          <Message type="note">Scenarios is disabled for proxy products.</Message>
        </Container>
      ) : (
        content[mode]
      )}
    </div>
  );
}
